<template>
  <v-dialog
      ref="dialog"
      :value="value"
      :persistent="busy"
      fullscreen
      hide-overlay
      scrollable
      transition="dialog-bottom-transition"
      @input="onDialogInput"
  >
    <v-card class="ie-card">
      <v-card-title class="ie-title">
        <span class="ie-crumbs">Stammdaten / Import /</span>
        <span class="font-weight-bold ml-1">{{ profileTitle }}</span>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="busy" aria-label="Schließen" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-stepper v-model="step" class="elevation-0 ie-stepper">
        <v-stepper-header class="elevation-0">
          <template v-for="(stepName, index) in stepNames">
            <v-stepper-step
                :key="'step-' + index"
                :step="index + 1"
                :complete="step > index + 1"
            >
              {{ stepName }}
            </v-stepper-step>
            <v-divider
                v-if="index < stepNames.length - 1"
                :key="'divider-' + index"
            ></v-divider>
          </template>
        </v-stepper-header>
      </v-stepper>
      <v-divider></v-divider>

      <v-card-text class="ie-body">
        <v-alert
            v-if="error"
            type="error"
            dense
            text
            dismissible
            class="mt-3 mb-0"
            @input="error = ''"
        >
          {{ error }}
        </v-alert>

        <!-- 1 Datei wählen -->
        <v-row v-if="step === 1" class="mt-1">
          <v-col cols="12" md="8">
            <v-select
                :items="profileItems"
                :value="selectedProfile"
                label="Was möchten Sie importieren?"
                :loading="profilesLoading"
                :disabled="busy || profilesLoading"
                :hint="profileItems.length ? '' : (profilesLoading ? 'Importtypen werden geladen …' : '')"
                persistent-hint
                outlined
                :menu-props="{offsetY: true}"
                class="mb-2"
                @change="onProfileChange"
            ></v-select>
            <v-alert v-if="noPermission" type="warning" dense text>
              Keine Berechtigung: Sie dürfen keine Stammdaten dieser Anwendung importieren.
            </v-alert>
            <div
                class="ie-drop"
                :class="{'ie-drop--over': dragOver, 'ie-drop--disabled': busy || !profileInfo}"
                role="button"
                tabindex="0"
                aria-label="Excel-Datei auswählen oder hier ablegen"
                @click="openPicker"
                @keydown.enter.prevent="openPicker"
                @keydown.space.prevent="openPicker"
                @dragenter.prevent="onDragOver"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
            >
              <v-icon size="48" color="primary">mdi-cloud-upload</v-icon>
              <div class="text-h6 mt-2">Excel-Datei hier ablegen</div>
              <div class="text-caption">Unterstützt .xlsx Arbeitsmappen<span v-if="maxUploadBytes"> bis {{ formatBytes(maxUploadBytes) }}</span></div>
              <v-btn
                  outlined
                  color="primary"
                  class="mt-3"
                  tabindex="-1"
                  :disabled="busy || !profileInfo"
                  @click.stop="openPicker"
              >
                Datei auswählen
              </v-btn>
              <input
                  ref="fileInput"
                  type="file"
                  class="d-none"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  @change="onNativeInput"
              >
            </div>
            <v-file-input
                v-model="pickedFile"
                accept=".xlsx"
                label="Excel-Datei (.xlsx)"
                prepend-icon="mdi-microsoft-excel"
                show-size
                class="mt-4"
                :disabled="busy || !profileInfo"
                @change="onFileInput"
            ></v-file-input>
            <v-alert
                v-for="(warning, index) in uploadWarnings"
                :key="'uw-' + index"
                type="warning"
                dense
                text
                class="mb-2"
            >
              {{ formatMessage(warning) }}
            </v-alert>
            <v-row v-if="upload">
              <template v-for="role in selectableRoles">
                <v-col :key="role + '-sheet'" cols="12" sm="8" class="pb-0">
                  <v-select
                      :items="sheetItems(role)"
                      :value="selections[role].sheet"
                      :label="sheetLabel(role)"
                      :disabled="busy"
                      dense
                      outlined
                      :menu-props="{offsetY: true}"
                      @change="onSheetChange(role, $event)"
                  ></v-select>
                </v-col>
                <v-col :key="role + '-header'" cols="12" sm="4" class="pb-0">
                  <v-select
                      :items="headerRowItems(role)"
                      :value="selections[role].headerRow"
                      label="Kopfzeile ab"
                      :disabled="busy || !selections[role].sheet"
                      dense
                      outlined
                      :menu-props="{offsetY: true}"
                      @change="onHeaderRowChange(role, $event)"
                  ></v-select>
                </v-col>
              </template>
            </v-row>
          </v-col>
          <v-col cols="12" md="4">
            <div class="ie-rules">
              <div class="ie-section-title">UPLOAD-REGELN</div>
              <p>
                Wählen Sie zuerst, welche Stammdaten Sie importieren möchten, dann die Arbeitsmappe. Falls die Datei mehrere Tabellenblätter enthält,
                wählen Sie unten das zu importierende Blatt aus.
              </p>
              <p>
                Der Import liest die Excel-Daten ab der definierten Kopfzeile aus. Die
                Spaltenüberschriften der Kopfzeile werden im nächsten Schritt zur Zuordnung verwendet.
              </p>
              <p v-if="dependentInfo">
                Enthält die Arbeitsmappe zusätzlich ein Blatt „{{ dependentInfo.label }}“, kann es
                mit ausgewählt werden. Es wird vor den {{ profileTitle }} importiert.
              </p>
              <p class="mb-0">
                Nur .xlsx<span v-if="maxUploadBytes">, höchstens {{ formatBytes(maxUploadBytes) }}</span>.
                Passwortgeschützte Dateien werden abgelehnt. Formeln werden mit ihrem zuletzt
                berechneten Wert übernommen.
              </p>
              <div v-if="upload" class="ie-file-card mt-4">
                <v-icon color="green darken-1" class="mr-2">mdi-microsoft-excel</v-icon>
                <div class="ie-file-card__text">
                  <div class="font-weight-bold ie-ellipsis" :title="upload.file_name">{{ upload.file_name }}</div>
                  <div class="text-caption">
                    .xlsx • {{ formatBytes(uploadSize) }} • {{ sheetCountText }}
                  </div>
                </div>
                <v-icon color="success">mdi-check</v-icon>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- 2 Zuordnung -->
        <div v-else-if="step === 2" class="mt-3">
          <v-row class="m-0" align="center">
            <v-col cols="12" md="8" class="pa-0 text-body-2">
              Ordnen Sie die erkannten Excel-Spalten den passenden Datenbankfeldern zu. Das Profil
              „{{ profileKey }}“ wendet erforderliche Mehrfach-Zuordnungen (Transformationen)
              automatisch an.
            </v-col>
            <v-col cols="12" md="4" class="pa-0 text-md-right">
              <v-chip
                  small
                  outlined
                  :color="missingRequired(currentRole).length ? 'error' : 'success'"
              >
                <v-icon left small>
                  {{ missingRequired(currentRole).length ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                </v-icon>
                {{ requiredChipText(currentRole) }}
              </v-chip>
            </v-col>
          </v-row>
          <v-tabs v-model="activeTab" height="40" class="mt-2">
            <v-tab v-for="role in activeRoles" :key="role">
              {{ roleTitle(role) }}
              <span class="ie-tab-sheet">· {{ selections[role].sheet }}</span>
              <v-icon v-if="missingRequired(role).length" small right color="error">mdi-alert-circle</v-icon>
            </v-tab>
          </v-tabs>
          <div v-if="currentPreview" class="ie-map-wrap">
            <table class="ie-map-table">
              <thead>
                <tr class="ie-map-select-row">
                  <th class="ie-map-rownum"></th>
                  <th v-for="col in currentPreview.columns" :key="'select-' + col.index">
                    <v-select
                        :items="fieldItems(currentRole, col.index)"
                        :value="mappingValue(currentRole, col.index)"
                        :aria-label="'Datenbankfeld für Spalte ' + col.letter"
                        :disabled="busy"
                        dense
                        outlined
                        hide-details
                        :menu-props="{offsetY: true}"
                        @change="setMapping(currentRole, col.index, $event)"
                    ></v-select>
                    <div
                        v-for="(hint, hintIndex) in columnHints(currentRole, col.index)"
                        :key="'hint-' + hintIndex"
                        class="ie-hint"
                    >
                      <v-icon x-small class="mr-1">{{ hint.icon }}</v-icon>{{ hint.text }}
                    </div>
                  </th>
                </tr>
                <tr>
                  <th class="ie-map-rownum">#</th>
                  <th v-for="col in currentPreview.columns" :key="'header-' + col.index">
                    {{ col.letter }}: {{ col.header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in currentPreview.rows" :key="'row-' + row.row">
                  <td class="ie-map-rownum">{{ row.row }}</td>
                  <td
                      v-for="col in currentPreview.columns"
                      :key="'cell-' + row.row + '-' + col.index"
                      :class="{'ie-map-unmapped': !mappingValue(currentRole, col.index)}"
                  >
                    {{ cellValue(row, col.index) }}
                  </td>
                </tr>
                <tr v-if="!currentPreview.rows.length">
                  <td :colspan="currentPreview.columns.length + 1" class="text-center grey--text">
                    Unterhalb der Kopfzeile wurden keine Datenzeilen gefunden.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="currentPreview" class="text-caption mt-1">
            Vorschau der ersten {{ currentPreview.rows.length }} von {{ currentPreview.total_rows }}
            Datenzeilen, Kopfzeile in Zeile {{ currentPreview.header_row }}.
          </div>
          <v-row v-if="showBillingType" class="mt-2">
            <v-col cols="12" md="6">
              <v-select
                  v-model="billingType"
                  :items="billingTypeItems"
                  :label="billingTypeLabel"
                  :hint="billingTypeHint"
                  persistent-hint
                  clearable
                  dense
                  outlined
                  :disabled="busy"
                  :menu-props="{offsetY: true}"
              ></v-select>
            </v-col>
          </v-row>
          <v-alert v-if="!bisIdMapped" type="info" dense text class="mt-3 mb-0">
            Die Spalte „BIS ID“ ist nicht zugeordnet. Ohne BIS ID können nur neue Datensätze angelegt werden.
          </v-alert>
        </div>

        <!-- 3 Prüfung -->
        <v-row v-else-if="step === 3" class="mt-1">
          <v-col cols="12" md="3">
            <div class="ie-section-title">IMPORT-MODUS</div>
            <div role="radiogroup" aria-label="Import-Modus">
              <v-card
                  v-for="m in modes"
                  :key="m.value"
                  outlined
                  class="ie-mode mb-3"
                  :class="{'ie-mode--active': mode === m.value}"
                  role="radio"
                  tabindex="0"
                  :aria-checked="mode === m.value ? 'true' : 'false'"
                  :disabled="busy"
                  @click="selectMode(m.value)"
                  @keydown.enter.prevent="selectMode(m.value)"
                  @keydown.space.prevent="selectMode(m.value)"
              >
                <div class="ie-mode__title">{{ m.title }}</div>
                <div class="ie-mode__text">{{ m.text }}</div>
              </v-card>
            </div>
            <v-btn block outlined color="primary" :disabled="busy" @click="runValidate">
              <v-icon left>mdi-refresh</v-icon>
              Erneut prüfen
            </v-btn>
          </v-col>
          <v-col cols="12" md="9">
            <div class="d-flex flex-wrap align-center">
              <div class="text-subtitle-1 font-weight-bold mr-4">{{ planTitle }}</div>
              <v-spacer></v-spacer>
              <template v-if="plan">
                <v-chip
                    v-for="chip in planChips"
                    :key="chip.key"
                    small
                    outlined
                    class="ma-1"
                    :color="chip.color"
                >
                  <v-icon left small>{{ chip.icon }}</v-icon>
                  {{ chip.value }} {{ chip.text }}
                </v-chip>
              </template>
            </div>
            <v-alert v-if="profileInfo && profileInfo.host_managed && !plan" type="warning" dense text class="mt-2 mb-0">
              Diese Stammdaten werden für Ihren Mandanten vom BIS-Host verwaltet. Importierte Änderungen
              können beim nächsten Abgleich überschrieben werden.
            </v-alert>
            <v-alert
                v-if="failedImport"
                type="error"
                dense
                text
                dismissible
                class="mt-2 mb-0"
                @input="failedImport = null"
            >
              <div class="d-flex flex-wrap align-center">
                <div class="mr-2">
                  <div class="font-weight-bold">
                    Import abgebrochen – Protokoll-ID <span class="ie-mono">{{ failedImport.id }}</span>
                  </div>
                  <div v-if="failedImport.message">{{ failedImport.message }}</div>
                  <div class="text-caption">Bitte prüfen Sie die Datei erneut, bevor Sie den Import wiederholen.</div>
                </div>
                <v-spacer></v-spacer>
                <v-btn small text color="error" :disabled="busy" @click="downloadFailedImportReport">
                  <v-icon left small>mdi-download</v-icon>
                  Fehlerbericht (Protokoll)
                </v-btn>
              </div>
            </v-alert>
            <v-alert v-if="mode === 'update' && !bisIdMapped" type="warning" dense text class="mt-2 mb-0">
              Für „{{ modeTitle(mode) }}“ muss die Spalte „BIS ID“ zugeordnet sein.
            </v-alert>
            <v-alert v-else-if="mode === 'upsert' && !bisIdMapped" type="info" dense text class="mt-2 mb-0">
              Ohne BIS-ID-Spalte werden alle Zeilen neu angelegt.
            </v-alert>
            <template v-if="plan">
              <v-alert
                  v-for="(warning, index) in plan.warnings || []"
                  :key="'pw-' + index"
                  type="warning"
                  dense
                  text
                  class="mt-2 mb-0"
              >
                {{ formatMessage(warning) }}
              </v-alert>
              <v-alert v-if="plan.counts && plan.counts.rename_cascades > 0" type="info" dense text class="mt-2 mb-0">
                Umbenennung betrifft {{ plan.counts.rename_cascades }} verknüpfte Datensätze.
              </v-alert>
              <v-alert v-if="newDependents.length" type="info" dense text class="mt-2 mb-0">
                Neu anzulegen: {{ newDependents.join(', ') }}
              </v-alert>
              <v-simple-table dense fixed-header height="360" class="ie-plan-table mt-3">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Zeile</th>
                      <th class="text-left">Aktion</th>
                      <th class="text-left">Name ({{ profileKey }})</th>
                      <th class="text-left">{{ detailHeader }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                        v-for="(row, index) in plan.rows || []"
                        :key="'plan-' + index"
                        :class="{'ie-row--reject': row.action === 'reject'}"
                    >
                      <td class="text-no-wrap">
                        {{ row.row }}
                        <div v-if="row.role === 'dependent'" class="text-caption grey--text">{{ row.sheet }}</div>
                      </td>
                      <td class="text-no-wrap">
                        <v-icon small :color="actionInfo(row.action).color">{{ actionInfo(row.action).icon }}</v-icon>
                        {{ actionInfo(row.action).text }}
                      </td>
                      <td>
                        <div>
                          {{ row.label || '—' }}
                          <span v-if="row.bis_id" class="text-caption grey--text ml-1">BIS ID {{ row.bis_id }}</span>
                        </div>
                        <div
                            v-for="(message, messageIndex) in row.messages || []"
                            :key="'msg-' + messageIndex"
                            class="text-caption"
                            :class="messageClass(message)"
                        >
                          <v-icon x-small :color="messageColor(message)">mdi-alert-circle</v-icon>
                          {{ formatRowMessage(message) }}
                        </div>
                      </td>
                      <td>
                        <div v-for="(lookup, lookupIndex) in rowLookups(row)" :key="'lookup-' + lookupIndex">
                          {{ lookup.value }}
                          <span v-if="lookup.isNew" class="primary--text text-caption ml-1">
                            <v-icon x-small color="primary">mdi-plus</v-icon>Neu anlegen
                          </span>
                        </div>
                        <div v-for="(diff, diffIndex) in row.diff || []" :key="'diff-' + diffIndex" class="text-caption">
                          {{ diff.field }}:
                          <span class="ie-old">{{ diff.old || '(leer)' }}</span>
                          <v-icon x-small>mdi-arrow-right</v-icon>
                          <span class="ie-new">{{ diff.new || '(leer)' }}</span>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!(plan.rows || []).length">
                      <td colspan="4" class="text-center grey--text">Keine Zeilen zu importieren.</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div v-if="(plan.rows || []).length < plan.total_rows" class="text-caption mt-1">
                Angezeigt werden alle abgewiesenen und die ersten weiteren Zeilen
                ({{ (plan.rows || []).length }} von {{ plan.total_rows }}). Der Fehlerbericht enthält alle Meldungen.
              </div>
            </template>
          </v-col>
        </v-row>

      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="ie-actions">
        <template v-if="step === 1">
          <span class="text-body-2 grey--text text--darken-1">
            {{ !profileInfo ? 'Bitte wählen Sie, was importiert werden soll.' : (upload ? 'Datei bereit für Zuordnung.' : 'Bitte wählen Sie eine .xlsx-Datei.') }}
          </span>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!canGoToMapping" @click="goToMapping">
            Weiter zur Zuordnung
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
        <template v-else-if="step === 2">
          <v-btn text :disabled="busy" @click="step = 1">
            <v-icon left>mdi-arrow-left</v-icon>
            Zurück
          </v-btn>
          <v-spacer></v-spacer>
          <span v-if="billingTypeMissing" class="red--text text-body-2 mr-3">
            <v-icon small color="error">mdi-alert</v-icon>
            Bitte eine Abrechnungsart wählen
          </span>
          <v-btn color="primary" :disabled="!canGoToValidation" @click="goToValidation">
            Weiter zur Prüfung
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
        <template v-else-if="step === 3">
          <v-btn text :disabled="busy" @click="step = 2">
            <v-icon left>mdi-arrow-left</v-icon>
            Zuordnung ändern
          </v-btn>
          <v-btn text color="primary" :disabled="busy || !plan" @click="downloadPlanReport">
            <v-icon left>mdi-download</v-icon>
            Fehlerbericht herunterladen
          </v-btn>
          <v-spacer></v-spacer>
          <span v-if="importBlocked" class="red--text text-body-2 mr-3">
            <v-icon small color="error">mdi-alert</v-icon>
            Import aufgrund von Fehlern gesperrt
          </span>
          <span v-else-if="plan && planStale" class="text-body-2 grey--text text--darken-1 mr-3">
            Bitte erneut prüfen
          </span>
          <v-btn color="primary" :disabled="!canImport" @click="runImport">
            Import starten
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Identical copy in web/auftragsbuch and web/zeitkontrolle, keep both in sync.
// The server is authoritative: this wizard only collects the selection and
// mapping and shows what the server returns (upload, preview, validate, import).
import {
  doImport,
  downloadAuditReport,
  downloadReport,
  errorMessage,
  formatBytes,
  formatMessage,
  getProfile,
  preview,
  upload,
  validate,
} from "@/shared/import_export_api";

const ACTIONS = {
  create: {icon: 'mdi-plus', text: 'Anlegen', color: 'success'},
  update: {icon: 'mdi-pencil', text: 'Aktualisieren', color: 'primary'},
  unchanged: {icon: 'mdi-equal', text: 'Unverändert', color: 'grey'},
  skip: {icon: 'mdi-skip-next-circle-outline', text: 'Übersprungen', color: 'grey'},
  reject: {icon: 'mdi-cancel', text: 'Abgewiesen', color: 'error'},
  reuse: {icon: 'mdi-check', text: 'Vorhanden', color: 'grey'},
};

// mode rules of the server (importer/validate.go resolveAction)
const MODES = [
  {value: 'create', title: 'Neu anlegen', text: 'Legt jede Zeile neu an, die BIS ID vergibt der Server. Zeilen mit BIS ID oder bereits vorhandener Bezeichnung (z. B. Name, Konto) werden abgewiesen.'},
  {value: 'update', title: 'Aktualisieren', text: 'Aktualisiert bestehende Datensätze anhand der „BIS ID“. Zeilen ohne oder mit unbekannter BIS ID werden abgewiesen.'},
  {value: 'upsert', title: 'Upsert', text: 'Zeilen mit BIS ID werden aktualisiert, Zeilen ohne BIS ID neu angelegt. Unbekannte BIS IDs werden abgewiesen.'},
];

function emptySelection() {
  return {sheet: '', headerRow: 1, preview: null, previewKey: '', mapping: {}};
}

// A rejected import answers with {error, audit, counts}; returns that body when
// it references the failed audit record, otherwise null.
function failedImportBody(response) {
  let body = response ? response.body : null;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch (e) {
      body = null;
    }
  }
  return body && body.audit && body.audit.id ? body : null;
}

export default {
  name: "StammdatenImportDialog",
  props: {
    // v-model: dialog open
    value: {
      type: Boolean,
      default: false,
    },
    // profile keys this application imports, e.g. ['LEGR', 'LEAR', 'GI_ARTI', 'HH']
    profiles: {
      type: Array,
      required: true,
    },
    apiPrefix: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      stepNames: ['Datei wählen', 'Zuordnung', 'Prüfung'],
      // ProfileDTO per profile key; a profile the user may not import is missing
      profileInfos: {},
      profilesLoaded: false,
      profilesLoading: false,
      selectedProfile: '',
      modes: MODES,
      step: 1,
      busy: false,
      loader: undefined,
      error: '',
      pickedFile: null,
      dragOver: false,
      upload: null,
      uploadSize: 0,
      selections: {
        main: emptySelection(),
        dependent: emptySelection(),
      },
      activeTab: 0,
      billingType: '',
      mode: 'create',
      plan: null,
      planStale: false,
      // {id, message} of the audit record of a rejected import
      failedImport: null,
      windowGuards: false,
    }
  },
  computed: {
    // ProfileDTO of the selected import type, null until one is chosen
    profileInfo() {
      return this.profileInfos[this.selectedProfile] || null;
    },
    profileItems() {
      return this.profiles.filter(key => this.profileInfos[key]).map(key => ({
        text: this.profileInfos[key].label || key,
        value: key,
      }));
    },
    noPermission() {
      return this.profilesLoaded && !this.profileItems.length;
    },
    profileKey() {
      return this.profileInfo ? this.profileInfo.key || this.selectedProfile : '';
    },
    profileTitle() {
      return this.profileInfo ? this.profileInfo.label || this.selectedProfile : 'Typ wählen';
    },
    dependentInfo() {
      return this.profileInfo ? this.profileInfo.dependent || null : null;
    },
    maxUploadBytes() {
      return this.profileInfo ? this.profileInfo.max_upload_bytes || 0 : 0;
    },
    sheets() {
      return this.upload && this.upload.sheets ? this.upload.sheets : [];
    },
    sheetCountText() {
      return this.sheets.length === 1 ? '1 Blatt erkannt' : this.sheets.length + ' Blätter erkannt';
    },
    uploadWarnings() {
      return this.upload && this.upload.warnings ? this.upload.warnings : [];
    },
    // the dependent sheet (LEAR→LEGR, MIGR→KOST) is offered for workbooks with more than one sheet
    dependentAvailable() {
      return !!this.dependentInfo && this.sheets.length > 1;
    },
    selectableRoles() {
      return this.dependentAvailable ? ['main', 'dependent'] : ['main'];
    },
    activeRoles() {
      return this.dependentAvailable && this.selections.dependent.sheet ? ['main', 'dependent'] : ['main'];
    },
    currentRole() {
      return this.activeRoles[this.activeTab] || 'main';
    },
    currentPreview() {
      return this.selections[this.currentRole].preview;
    },
    canGoToMapping() {
      return !this.busy && !!this.profileInfo && !!this.upload && !!this.selections.main.sheet;
    },
    hasMainMapping() {
      const mapping = this.selections.main.mapping;
      return Object.keys(mapping).some(index => !!mapping[index]);
    },
    canGoToValidation() {
      return !this.busy && this.hasMainMapping && !this.billingTypeMissing;
    },
    bisIdMapped() {
      const field = this.roleFields('main').find(f => f.kind === 'bisid' || f.required_in_update);
      return !field || this.isMapped('main', field.key);
    },
    billingTypes() {
      if (!this.profileInfo) {
        return [];
      }
      if (this.profileInfo.billing_types && this.profileInfo.billing_types.length) {
        return this.profileInfo.billing_types;
      }
      return this.dependentInfo && this.dependentInfo.billing_types ? this.dependentInfo.billing_types : [];
    },
    billingTypeItems() {
      return this.billingTypes.map(option => ({
        text: option.label && option.label !== option.value ? option.label + ' (' + option.value + ')' : option.value,
        value: option.value,
      }));
    },
    // LEGR: billing_type column of the main sheet; LEAR: of the optional Leistungsgruppen sheet
    billingTypeMapped() {
      return this.activeRoles.some(role => {
        const field = this.roleFields(role).find(f => f.constant_allowed);
        return !!field && this.isMapped(role, field.key);
      });
    },
    // The picked billing type is used by the server only for new groups
    // (importer/validate.go): LEGR rows created with an empty or unmapped
    // Abrechnungsart and, for LEAR, groups created automatically for unknown
    // Leistungsgruppen. Existing groups keep their billing type.
    showBillingType() {
      return this.billingTypes.length > 0;
    },
    // LEGR without Abrechnungsart column: new groups get their billing type only from here
    billingTypeRequired() {
      return this.showBillingType && this.profileKey === 'LEGR' && !this.billingTypeMapped;
    },
    billingTypeMissing() {
      return this.billingTypeRequired && !this.billingType;
    },
    billingTypeLabel() {
      if (this.profileKey === 'LEAR') {
        return 'Abrechnungsart für automatisch angelegte Leistungsgruppen';
      }
      if (this.billingTypeMapped) {
        return 'Abrechnungsart für neue Zeilen ohne Wert (optional)';
      }
      return 'Abrechnungsart für neue Leistungsgruppen *';
    },
    billingTypeHint() {
      if (this.profileKey === 'LEAR') {
        const sheet = this.activeRoles.indexOf('dependent') !== -1 ? this.selections.dependent.sheet : '';
        return 'Gilt für Leistungsgruppen, die noch nicht existieren und automatisch angelegt werden'
            + (sheet ? ', sowie für neue Zeilen im Blatt „' + sheet + '“ ohne Abrechnungsart' : '')
            + '. Ohne Auswahl werden solche Zeilen abgewiesen. Bestehende Leistungsgruppen behalten ihre Abrechnungsart.';
      }
      if (this.billingTypeMapped) {
        return 'Gilt nur für neue Leistungsgruppen, deren Zelle „Abrechnungsart“ leer ist; ohne Auswahl werden sie abgewiesen. '
            + 'Bestehende Leistungsgruppen behalten bei leerer Zelle ihre Abrechnungsart.';
      }
      return 'Pflichtangabe, da die Spalte „Abrechnungsart“ nicht zugeordnet ist. Gilt für alle neu angelegten Leistungsgruppen; '
          + 'bestehende behalten ihre Abrechnungsart.';
    },
    planTitle() {
      if (this.busy && !this.plan) {
        return 'Validierung läuft …';
      }
      return this.plan ? 'Validierung abgeschlossen' : 'Noch nicht geprüft';
    },
    planChips() {
      const counts = this.plan && this.plan.counts ? this.plan.counts : {};
      return [
        {key: 'created', text: 'Anlegen', icon: 'mdi-plus', color: 'success', value: counts.created || 0},
        {key: 'updated', text: 'Aktualisieren', icon: 'mdi-pencil', color: 'primary', value: counts.updated || 0},
        {key: 'unchanged', text: 'Unverändert', icon: 'mdi-equal', color: 'grey darken-1', value: counts.unchanged || 0},
        {key: 'dependents', text: 'Gruppen anlegen', icon: 'mdi-sitemap', color: 'blue-grey', value: counts.dependents_created || 0},
        {key: 'skipped', text: 'Übersprungen', icon: 'mdi-skip-next-circle-outline', color: 'grey darken-1', value: counts.skipped || 0},
        {key: 'rejected', text: 'Abgewiesen', icon: 'mdi-alert', color: 'error', value: counts.rejected || 0},
      ].filter(chip => chip.value > 0 || chip.key === 'rejected')
          .map(chip => chip.key === 'rejected' && !chip.value ? Object.assign({}, chip, {color: 'grey darken-1'}) : chip);
    },
    newDependents() {
      if (!this.plan || !this.plan.dependents) {
        return [];
      }
      return this.plan.dependents.filter(d => d.action === 'create').map(d => d.name);
    },
    detailHeader() {
      return this.dependentInfo ? 'Zuordnung (' + (this.dependentInfo.key || '') + ') / Details' : 'Details';
    },
    importBlocked() {
      return !!this.plan && !this.plan.can_import;
    },
    canImport() {
      return !this.busy && !!this.plan && !!this.plan.can_import && !this.planStale
          && (!this.plan.mode || this.plan.mode === this.mode);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          this.onOpen();
        } else {
          this.removeWindowGuards();
        }
      },
    },
    step() {
      this.error = '';
    },
    activeRoles(roles) {
      if (this.activeTab >= roles.length) {
        this.activeTab = 0;
      }
    },
  },
  mounted() {
    this.raiseStack();
  },
  beforeDestroy() {
    this.removeWindowGuards();
    this.hideLoader();
  },
  methods: {
    // CoreUI's fixed header/sidebar use z-index 1020/1019 in some apps, above
    // Vuetify's default dialog stack (202), so lift this dialog and its overlay.
    raiseStack() {
      const dialog = this.$refs.dialog;
      if (!dialog) {
        return;
      }
      dialog.stackMinZIndex = 1030;
      this.$nextTick(() => {
        if (dialog.overlay && dialog.activeZIndex) {
          dialog.overlay.zIndex = String(dialog.activeZIndex - 1);
        }
      });
    },
    formatBytes(bytes) {
      return formatBytes(bytes);
    },
    onOpen() {
      // jexcel listens to keydown on the document and would take the keystrokes
      // typed into the dialog while a grid cell is still selected
      if (window.jexcel) {
        if (window.jexcel.current && typeof window.jexcel.current.resetSelection === 'function') {
          try {
            window.jexcel.current.resetSelection(true);
          } catch (e) {
            // the grid may already be detached
          }
        }
        window.jexcel.current = null;
      }
      this.addWindowGuards();
      if (!this.profilesLoaded && !this.profilesLoading) {
        this.loadProfiles();
      }
    },
    // The profile endpoint is the permission gate: only profiles the user may
    // import are offered. With a single profile it is preselected.
    loadProfiles() {
      this.profilesLoading = true;
      Promise.all(this.profiles.map(key => getProfile(this.apiPrefix, key).then(
          response => ({key: key, info: response.body || null}),
          () => ({key: key, info: null}),
      ))).then(results => {
        const infos = {};
        results.forEach(result => {
          if (result.info) {
            infos[result.key] = result.info;
          }
        });
        this.profileInfos = infos;
        this.profilesLoading = false;
        this.profilesLoaded = true;
        const available = this.profiles.filter(key => infos[key]);
        if (available.length === 1) {
          this.onProfileChange(available[0]);
        }
      });
    },
    onProfileChange(key) {
      if (this.busy || key === this.selectedProfile) {
        return;
      }
      this.selectedProfile = key || '';
      this.pickedFile = null;
      this.resetUpload();
      this.billingType = '';
      this.mode = 'create';
      this.error = '';
      this.step = 1;
    },
    onDialogInput(open) {
      if (!open) {
        this.close();
      }
    },
    close() {
      if (this.busy) {
        return;
      }
      this.removeWindowGuards();
      this.$emit('input', false);
    },

    // --- busy state and errors ---
    startBusy() {
      this.busy = true;
      this.error = '';
      if (this.loader === undefined) {
        this.loader = this.$loading.show();
      }
    },
    endBusy() {
      this.busy = false;
      this.hideLoader();
    },
    hideLoader() {
      if (this.loader !== undefined) {
        this.loader.hide();
        this.loader = undefined;
      }
    },
    fail(response) {
      this.endBusy();
      return errorMessage(response).then(message => {
        if (message) {
          this.error = message;
        }
      });
    },

    // --- step 1: file ---
    // a drop outside the drop zone must not make the browser open the file
    preventWindowDrop(event) {
      event.preventDefault();
    },
    addWindowGuards() {
      if (!this.windowGuards) {
        window.addEventListener('dragover', this.preventWindowDrop);
        window.addEventListener('drop', this.preventWindowDrop);
        this.windowGuards = true;
      }
    },
    removeWindowGuards() {
      if (this.windowGuards) {
        window.removeEventListener('dragover', this.preventWindowDrop);
        window.removeEventListener('drop', this.preventWindowDrop);
        this.windowGuards = false;
      }
    },
    openPicker() {
      if (!this.busy && this.profileInfo && this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onDragOver(event) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = this.busy ? 'none' : 'copy';
      }
      this.dragOver = !this.busy;
    },
    onDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOver = false;
      }
    },
    onDrop(event) {
      this.dragOver = false;
      if (this.busy) {
        return;
      }
      const files = event.dataTransfer ? event.dataTransfer.files : null;
      if (!files || !files.length) {
        return;
      }
      if (files.length > 1) {
        this.error = 'Bitte legen Sie nur eine Datei ab.';
        return;
      }
      this.handleFile(files[0]);
    },
    onNativeInput(event) {
      const file = event.target.files && event.target.files[0];
      // allow picking the same file again
      event.target.value = '';
      if (file) {
        this.handleFile(file);
      }
    },
    onFileInput(file) {
      if (file) {
        this.handleFile(file);
      } else {
        this.resetUpload();
      }
    },
    checkFile(file) {
      if (!/\.xlsx$/i.test(file.name || '')) {
        return 'Es werden nur Excel-Arbeitsmappen im Format .xlsx unterstützt.';
      }
      if (!file.size) {
        return 'Die Datei ist leer.';
      }
      if (this.maxUploadBytes && file.size > this.maxUploadBytes) {
        return 'Die Datei ist zu groß (' + formatBytes(file.size) + '). Erlaubt sind höchstens ' + formatBytes(this.maxUploadBytes) + '.';
      }
      return '';
    },
    handleFile(file) {
      if (this.busy) {
        return;
      }
      if (!this.profileInfo) {
        this.error = 'Bitte wählen Sie zuerst, was importiert werden soll.';
        this.pickedFile = null;
        return;
      }
      const problem = this.checkFile(file);
      if (problem) {
        this.error = problem;
        this.pickedFile = null;
        return;
      }
      this.pickedFile = file;
      this.resetUpload();
      this.startBusy();
      upload(this.apiPrefix, this.selectedProfile, file).then(response => {
        this.endBusy();
        this.applyUpload(response.body, file);
      }, response => {
        this.pickedFile = null;
        return this.fail(response);
      });
    },
    resetUpload() {
      this.upload = null;
      this.uploadSize = 0;
      this.selections.main = emptySelection();
      this.selections.dependent = emptySelection();
      this.activeTab = 0;
      this.plan = null;
      this.planStale = false;
      this.failedImport = null;
    },
    applyUpload(result, file) {
      this.upload = result || {};
      this.uploadSize = file.size;
      const suggested = this.upload.suggested || {};
      let main = this.sheetExists(suggested.main) ? suggested.main : '';
      if (!main) {
        const firstFilled = this.sheets.find(sheet => sheet.first_non_empty_row > 0) || this.sheets[0];
        main = firstFilled ? firstFilled.name : '';
      }
      this.setSelection('main', main);
      let dependent = '';
      if (this.dependentAvailable && this.sheetExists(suggested.dependent) && suggested.dependent !== main) {
        dependent = suggested.dependent;
      }
      this.setSelection('dependent', dependent);
    },
    sheetExists(name) {
      return !!name && this.sheets.some(sheet => sheet.name === name);
    },
    sheetInfo(name) {
      return this.sheets.find(sheet => sheet.name === name) || null;
    },
    setSelection(role, sheetName) {
      const info = this.sheetInfo(sheetName);
      const selection = emptySelection();
      selection.sheet = sheetName || '';
      selection.headerRow = info && info.first_non_empty_row > 0 ? info.first_non_empty_row : 1;
      this.selections[role] = selection;
      this.plan = null;
    },
    onSheetChange(role, sheetName) {
      this.setSelection(role, sheetName);
      if (role === 'main' && this.selections.dependent.sheet === sheetName) {
        this.setSelection('dependent', '');
      }
    },
    onHeaderRowChange(role, headerRow) {
      this.selections[role].headerRow = headerRow;
      this.plan = null;
    },
    sheetLabel(role) {
      if (role === 'dependent') {
        return 'Blatt für ' + this.roleTitle(role) + ' (optional)';
      }
      return this.dependentAvailable ? 'Blatt für ' + this.profileTitle : 'Aktives Tabellenblatt';
    },
    sheetItems(role) {
      const items = this.sheets.map(sheet => ({
        text: sheet.first_non_empty_row > 0 ? sheet.name : sheet.name + ' (leer)',
        value: sheet.name,
      }));
      if (role === 'dependent') {
        return [{text: '— kein Blatt —', value: ''}].concat(items.filter(item => item.value !== this.selections.main.sheet));
      }
      return items;
    },
    headerRowItems(role) {
      const selection = this.selections[role];
      const info = this.sheetInfo(selection.sheet);
      const max = Math.min(20, Math.max(info ? info.max_row : 1, 1));
      const items = [];
      for (let row = 1; row <= max; row++) {
        items.push({text: 'Zeile ' + row, value: row});
      }
      if (selection.headerRow > max) {
        items.push({text: 'Zeile ' + selection.headerRow, value: selection.headerRow});
      }
      return items;
    },

    // --- step 2: mapping ---
    roleInfo(role) {
      return role === 'dependent' ? (this.dependentInfo || {}) : (this.profileInfo || {});
    },
    roleTitle(role) {
      const info = this.roleInfo(role);
      return info.label || info.key || role;
    },
    roleFields(role) {
      return this.roleInfo(role).fields || [];
    },
    fieldByKey(role, key) {
      return this.roleFields(role).find(field => field.key === key) || null;
    },
    previewKey(role) {
      const selection = this.selections[role];
      return selection.sheet + '|' + selection.headerRow;
    },
    goToMapping() {
      if (!this.canGoToMapping) {
        return;
      }
      // keep the mapping of sheets whose sheet and header row did not change
      const pending = this.activeRoles.filter(role => this.selections[role].previewKey !== this.previewKey(role));
      if (!pending.length) {
        this.activeTab = 0;
        this.step = 2;
        return;
      }
      this.startBusy();
      const token = this.upload.token;
      Promise.all(pending.map(role => preview(this.apiPrefix, this.selectedProfile, {
        token: token,
        role: role,
        sheet: this.selections[role].sheet,
        header_row: this.selections[role].headerRow,
      }).then(response => ({role: role, body: response.body})))).then(results => {
        this.endBusy();
        results.forEach(result => this.applyPreview(result.role, result.body));
        this.plan = null;
        this.activeTab = 0;
        this.step = 2;
      }, this.fail);
    },
    applyPreview(role, body) {
      const selection = this.selections[role];
      const allowed = {};
      this.roleFields(role).forEach(field => {
        allowed[field.key] = true;
      });
      const proposed = body && body.proposed_mapping ? body.proposed_mapping : {};
      const mapping = {};
      const used = {};
      Object.keys(proposed).forEach(index => {
        const key = proposed[index];
        if (key && allowed[key] && !used[key]) {
          mapping[index] = key;
          used[key] = true;
        }
      });
      const result = Object.assign({total_rows: 0}, body || {});
      // Go encodes empty slices as null
      result.columns = result.columns || [];
      result.rows = result.rows || [];
      selection.preview = result;
      selection.previewKey = this.previewKey(role);
      selection.mapping = mapping;
    },
    mappingValue(role, columnIndex) {
      return this.selections[role].mapping[String(columnIndex)] || '';
    },
    isMapped(role, key) {
      const mapping = this.selections[role].mapping;
      return Object.keys(mapping).some(index => mapping[index] === key);
    },
    setMapping(role, columnIndex, key) {
      const mapping = Object.assign({}, this.selections[role].mapping);
      const index = String(columnIndex);
      if (key) {
        // every field can be mapped only once
        Object.keys(mapping).forEach(other => {
          if (mapping[other] === key) {
            delete mapping[other];
          }
        });
        mapping[index] = key;
      } else {
        delete mapping[index];
      }
      this.selections[role].mapping = mapping;
      this.plan = null;
    },
    fieldItems(role, columnIndex) {
      const mapping = this.selections[role].mapping;
      const index = String(columnIndex);
      const usedElsewhere = {};
      Object.keys(mapping).forEach(other => {
        if (other !== index && mapping[other]) {
          usedElsewhere[mapping[other]] = true;
        }
      });
      return [{text: '— nicht importieren —', value: ''}].concat(this.roleFields(role).map(field => ({
        text: field.label + (field.required ? ' *' : ''),
        value: field.key,
        disabled: !!usedElsewhere[field.key],
      })));
    },
    isLookupTo(role, field, profileKey, targetKey) {
      return role === 'main' && this.profileKey === profileKey && field.kind === 'lookup'
          && (field.key === targetKey || (field.targets || []).indexOf(targetKey) !== -1);
    },
    columnHints(role, columnIndex) {
      const key = this.mappingValue(role, columnIndex);
      const field = key ? this.fieldByKey(role, key) : null;
      if (!field) {
        return [];
      }
      const hints = [];
      if (field.targets && field.targets.length > 1) {
        hints.push({icon: 'mdi-pencil', text: 'Kopiert nach: ' + field.targets.slice(1).join(', ')});
      }
      if (this.isLookupTo(role, field, 'LEAR', 'legr')) {
        hints.push({icon: 'mdi-sitemap', text: 'Fehlende Gruppen werden mit angelegt'});
      }
      if (this.isLookupTo(role, field, 'MIGR', 'kost')) {
        hints.push({icon: 'mdi-sitemap', text: 'Kostenstelle muss vorhanden sein oder im Kostenstellen-Blatt stehen'});
      }
      if (field.hint) {
        hints.push({icon: 'mdi-information-outline', text: field.hint});
      }
      return hints;
    },
    missingRequired(role) {
      return this.roleFields(role).filter(field => field.required && !this.isMapped(role, field.key)
          && !(field.constant_allowed && this.billingType));
    },
    requiredChipText(role) {
      const missing = this.missingRequired(role);
      if (missing.length) {
        return 'Pflichtfelder fehlen: ' + missing.map(field => field.label).join(', ');
      }
      const count = this.roleFields(role).filter(field => field.required).length;
      return 'Alle Pflichtfelder (' + count + ') zugeordnet';
    },
    cellValue(row, columnIndex) {
      const cells = row.cells || [];
      return cells[columnIndex] !== undefined && cells[columnIndex] !== null ? cells[columnIndex] : '';
    },
    buildRequest() {
      return {
        token: this.upload ? this.upload.token : '',
        mode: this.mode,
        // the server applies it to new groups only, a mapped Abrechnungsart cell wins
        billing_type: this.showBillingType && this.billingType ? this.billingType : '',
        sheets: this.activeRoles.map(role => {
          const selection = this.selections[role];
          const mapping = {};
          Object.keys(selection.mapping).forEach(index => {
            if (selection.mapping[index]) {
              mapping[index] = selection.mapping[index];
            }
          });
          return {role: role, sheet: selection.sheet, header_row: selection.headerRow, mapping: mapping};
        }),
      };
    },
    goToValidation() {
      if (!this.canGoToValidation) {
        return;
      }
      this.step = 3;
      this.runValidate();
    },

    // --- step 3: validation ---
    modeTitle(mode) {
      const found = MODES.find(m => m.value === mode);
      return found ? found.title : (mode || '—');
    },
    selectMode(mode) {
      if (this.busy || this.mode === mode) {
        return;
      }
      this.mode = mode;
      this.plan = null;
      this.runValidate();
    },
    runValidate() {
      if (this.busy || !this.upload) {
        return;
      }
      const request = this.buildRequest();
      this.plan = null;
      this.startBusy();
      validate(this.apiPrefix, this.selectedProfile, request).then(response => {
        this.endBusy();
        this.plan = response.body;
        this.planStale = false;
      }, this.fail);
    },
    actionInfo(action) {
      return ACTIONS[action] || {icon: 'mdi-information-outline', text: action || '—', color: 'grey'};
    },
    messageColor(message) {
      if (message.level === 'warning') {
        return 'orange darken-2';
      }
      return message.level === 'info' ? 'grey' : 'error';
    },
    messageClass(message) {
      if (message.level === 'warning') {
        return 'orange--text text--darken-3';
      }
      return message.level === 'info' ? 'grey--text text--darken-1' : 'red--text';
    },
    formatRowMessage(message) {
      const prefix = message.column ? 'Spalte ' + message.column + (message.field ? ' (' + message.field + ')' : '') + ': ' : '';
      return prefix + (message.text || '');
    },
    formatMessage(message) {
      return formatMessage(message);
    },
    // value of lookup fields (e.g. Leistungsgruppe) and whether the referenced group is created
    rowLookups(row) {
      const role = row.role === 'dependent' ? 'dependent' : 'main';
      const values = row.values || {};
      const created = {};
      ((this.plan && this.plan.dependents) || []).forEach(dependent => {
        if (dependent.action === 'create') {
          created[(dependent.name || '').toLowerCase()] = true;
        }
      });
      return this.roleFields(role)
          .filter(field => field.kind === 'lookup' && values[field.key])
          .map(field => ({
            value: values[field.key],
            isNew: role === 'main' && !!created[String(values[field.key]).toLowerCase()],
          }));
    },
    downloadPlanReport() {
      if (this.busy || !this.upload) {
        return;
      }
      this.startBusy();
      downloadReport(this.apiPrefix, this.selectedProfile, this.upload.token).then(this.endBusy, this.fail);
    },
    runImport() {
      if (!this.canImport) {
        return;
      }
      this.failedImport = null;
      this.startBusy();
      doImport(this.apiPrefix, this.selectedProfile, this.buildRequest()).then(response => {
        this.endBusy();
        // no result step: the page shows the counts and warnings
        this.$emit('imported', response.body || {});
        this.close();
      }, response => {
        // the server re-validates inside the import; require a fresh check before retrying
        this.planStale = true;
        const failed = failedImportBody(response);
        if (!failed) {
          return this.fail(response);
        }
        // rolled back; the failed import has its own audit record with error report
        this.endBusy();
        const text = typeof failed.error === 'string' ? failed.error : '';
        return (text ? Promise.resolve(text) : errorMessage(response)).then(message => {
          this.failedImport = {id: String(failed.audit.id), message: message};
        });
      });
    },
    downloadFailedImportReport() {
      if (this.busy || !this.failedImport) {
        return;
      }
      this.startBusy();
      downloadAuditReport(this.apiPrefix, this.failedImport.id).then(this.endBusy, this.fail);
    },
  },
}
</script>

<style scoped>

.ie-card ::v-deep label {
  margin-bottom: initial;
}

.ie-title {
  font-size: 1rem !important;
  padding-bottom: 8px !important;
}

.ie-crumbs {
  color: rgba(0, 0, 0, 0.6);
  letter-spacing: 0.05em;
}

.ie-stepper {
  flex: 0 0 auto;
}

.ie-stepper ::v-deep .v-stepper__header {
  height: 56px;
  box-shadow: none;
}

.ie-stepper ::v-deep .v-stepper__step {
  padding: 12px 24px;
}

.ie-body {
  color: rgba(0, 0, 0, 0.87) !important;
}

.ie-actions {
  padding: 12px 24px;
}

.ie-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #3f51b5;
  margin-bottom: 8px;
}

.ie-drop {
  border: 2px dashed #3f51b5;
  border-radius: 4px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s;
}

.ie-drop:focus-visible,
.ie-drop:focus {
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.35);
}

.ie-drop--over {
  background-color: rgba(63, 81, 181, 0.08);
}

.ie-drop--disabled {
  cursor: default;
  opacity: 0.6;
}

.ie-rules {
  background-color: #f5f6fa;
  border-radius: 4px;
  padding: 16px;
  font-size: 0.8125rem;
  height: 100%;
}

.ie-file-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
}

.ie-file-card__text {
  flex: 1 1 auto;
  min-width: 0;
}

.ie-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ie-tab-sheet {
  margin-left: 6px;
  text-transform: none;
  color: rgba(0, 0, 0, 0.54);
}

.ie-map-wrap {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-top: none;
}

.ie-map-table {
  border-collapse: collapse;
  min-width: 100%;
  font-size: 0.8125rem;
}

.ie-map-table th,
.ie-map-table td {
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 10px;
  text-align: left;
  vertical-align: top;
}

.ie-map-table th {
  background-color: #f5f6fa;
  font-weight: 600;
  white-space: nowrap;
}

.ie-map-select-row th {
  min-width: 210px;
  font-weight: normal;
  white-space: normal;
}

.ie-map-table .ie-map-rownum {
  min-width: 48px;
  width: 48px;
  color: rgba(0, 0, 0, 0.54);
  text-align: right;
}

.ie-map-unmapped {
  color: rgba(0, 0, 0, 0.38);
}

.ie-hint {
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}

.ie-mode {
  padding: 10px 12px;
  cursor: pointer;
  outline: none;
}

.ie-mode:focus {
  box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.35) !important;
}

.ie-mode--active {
  border: 2px solid #3f51b5 !important;
  background-color: rgba(63, 81, 181, 0.06) !important;
}

.ie-mode__title {
  font-weight: 700;
  font-size: 0.875rem;
}

.ie-mode__text {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}

.ie-plan-table ::v-deep td {
  vertical-align: top;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  height: auto !important;
}

.ie-row--reject {
  background-color: #fdecea;
}

.ie-old {
  text-decoration: line-through;
  color: rgba(0, 0, 0, 0.54);
}

.ie-new {
  font-weight: 600;
}

.ie-mono {
  font-family: monospace;
  word-break: break-all;
}

</style>
